import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Target, BookOpen, FileText, BarChart3, Wrench, Zap } from "lucide-react";
import { BRAND_PURPLE } from "../../constants/colors";
import { api1 } from "../../utils/axiosInstance";
import { API_ENDPOINTS } from "../../services/apiEndpoints";

const PathwayCard = () => {
  const navigate = useNavigate();
  
  const { targetJob, proficiencyLevel, quizCompleted } = useSelector((state) => state.user);
  const [skillGapCount, setSkillGapCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchSkillGapCount = async () => {
      try {
        const response = await api1.get(API_ENDPOINTS.GET_LATEST_SKILL_GAP_COUNT);
        if (response.data.success) {
          setSkillGapCount(response.data.data.skillsGapCount || 0);
        }
      } catch (error) {
        console.error("Error fetching skill gap count:", error);
        setSkillGapCount(0);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSkillGapCount();
  }, []);
  
  const pathwayCards = [
    {
      type: 'target',
      title: 'Target Career',
      value: targetJob || 'Not Selected',
      subtitle: targetJob ? 'Your personalized pathway' : 'Select your target career to begin',
      buttonText: 'View →',
      onClick: () => navigate('/dashboard/career-discovery/career-recommendations')
    },
    {
      type: 'literacy',
      title: 'AI Literacy Level',
      value: quizCompleted ? proficiencyLevel : 'Not Assessed',
      subtitle: quizCompleted ? 'Assessment completed' : 'Complete assessment to see your level',
      buttonText: 'View →',
      onClick: () => navigate('/dashboard/career-assessment/ai-literacy-assessment')
    },
    {
      type: 'skills',
      title: 'Skills Gap Analysis',
      value: isLoading ? 'Loading...' : `${skillGapCount} Priority Gaps`,
      subtitle: isLoading ? 'Fetching analysis...' : (skillGapCount > 0 ? 'Focus areas identified' : 'No gaps identified yet'),
      buttonText: 'View →',
      onClick: () => navigate('/dashboard/skills-and-learning/skill-gap-analysis-tool')
    },
  ];

  const getIcon = (type) => {
    const icons = { target: Target, literacy: BookOpen, courses: BookOpen, simulation: FileText, skills: BarChart3, tools: Zap };
    return icons[type] || Target;
  };

  const getTheme = (type) => {
    const themes = {
      target: { border: "border-gray-200", titleText: BRAND_PURPLE, iconText: "text-white", iconBg: BRAND_PURPLE, text: "text-purple-900", button: BRAND_PURPLE },
      literacy: { border: "border-gray-200", titleText: BRAND_PURPLE, iconText: "text-white", iconBg: BRAND_PURPLE, text: "text-purple-900", button: BRAND_PURPLE },
      courses: { border: "border-gray-200", titleText: BRAND_PURPLE, iconText: "text-white", iconBg: BRAND_PURPLE, text: "text-purple-900", button: BRAND_PURPLE },
      simulation: { border: "border-gray-200", titleText: BRAND_PURPLE, iconText: "text-white", iconBg: BRAND_PURPLE, text: "text-purple-900", button: BRAND_PURPLE },
      skills: { border: "border-gray-200", titleText: BRAND_PURPLE, iconText: "text-white", iconBg: BRAND_PURPLE, text: "text-purple-900", button: BRAND_PURPLE },
      tools: { border: "border-gray-200", titleText: BRAND_PURPLE, iconText: "text-white", iconBg: BRAND_PURPLE, text: "text-purple-900", button: BRAND_PURPLE }
    };
    return themes[type] || themes.target;
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {pathwayCards.map((card, index) => {
        const Icon = getIcon(card.type);
        const theme = getTheme(card.type);
        
        return (
          <div key={index} className={`bg-white p-6 rounded-lg border-2 border-gray-300 shadow-md hover:shadow-lg transition-shadow`}>
            <div className="flex items-center justify-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: theme.iconBg }}>
                <Icon className={`w-4 h-4 ${theme.iconText}`} />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-base text-gray-900" style={{ color: theme.titleText }}>{card.title}</h3>
              </div>
            </div>
            <div>
              <p className={`text-lg font-bold ${theme.text} mb-1`}>{card.value}</p>
              <p className={`text-sm text-gray-500 mb-2`}>{card.subtitle}</p>

              {card.buttonText && (
                <button 
                  onClick={card.onClick}
                  className="text-sm font-medium transition-colors"
                  style={{ color: theme.button }}
                >
                  {card.buttonText}
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default PathwayCard; 